import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ChevronDown, Pill, BookHeart, Settings, PawPrint } from 'lucide-react';
import { cn } from '../lib/utils';
import { useLanguage } from '../functions/useLanguage';
import { NotificationCenter } from './NotificationCenter';
import { PetSwitcher } from './PetSwitcher';
import { usePet } from '../context/PetContext';
import { useBean } from '../context/BeanContext';
import { PetSpecies, PET_SPECIES_EMOJI } from '../types';
import BeanIcon from './Custom Icons/KodaIcon';
import DogIcon from './Custom Icons/dog_icon';
import CatIcon from './Custom Icons/cat_icon';
import RabbitIcon from './Custom Icons/rabbit_icon';
import ChickenIcon from './Custom Icons/chicken_icon';
import HorseIcon from './Custom Icons/horse_icon';
import GoatIcon from './Custom Icons/goat_icon';
import SheepIcon from './Custom Icons/sheep_icon';
import PigIcon from './Custom Icons/pig_icon';
import ReptileIcon from './Custom Icons/reptile_icon';
import OtherAnimalIcon from './Custom Icons/otheranimal_icon';


interface TopBarProps {
  userName: string;
  scrollable?: boolean;
  walkthroughActive?: boolean;
}

const SPECIES_ICONS: Partial<Record<PetSpecies, React.ComponentType<React.SVGProps<SVGSVGElement>>>> = {
  dog: DogIcon,
  cat: CatIcon,
  rabbit: RabbitIcon,
  chicken: ChickenIcon,
  horse: HorseIcon,
  goat: GoatIcon,
  sheep: SheepIcon,
  pig: PigIcon,
  reptile: ReptileIcon,
  otheranimal: OtherAnimalIcon,
  // legacy fallback mappings
  bird: ChickenIcon,
  fish: ReptileIcon,
  other: OtherAnimalIcon,
};

function TopBarSpeciesIcon({ species, color }: { species: PetSpecies; color: string }) {
  const Icon = SPECIES_ICONS[species];
  if (Icon) {
    return (
      <Icon
        className="pet-species-svg w-7 h-7 shrink-0 text-current [&_.cls-1]:fill-current [&_.cls-2]:fill-current [&_.cls-3]:fill-current [&_path]:fill-current [&_ellipse]:fill-current [&_circle]:fill-current"
        style={{ color }}
      />
    );
  }
  return (
    <span className="w-7 h-7 shrink-0 inline-flex items-center justify-center text-lg" style={{ color }}>
      {PET_SPECIES_EMOJI[species]}
    </span>
  );
}

export function TopBar({ userName, scrollable, walkthroughActive }: TopBarProps) {
  const { t } = useLanguage();
  const location = useLocation();
  const { activePet } = usePet();
  const { profile } = useBean();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const petChipLabel = activePet?.name ?? 'Your Pet';
  const petChipColor = activePet?.color ?? '#9CA3AF';

  return (
    <>
      <header className={cn(
        "w-full h-10 px-4 z-50",
        scrollable ? "relative" : "sm:sticky sm:top-0 sm:h-16",
        "bg-white")}>
        <div className="max-w-[1280px] mx-auto w-full py-2 flex justify-between items-center relative">

        {/* Left: Bean icon (desktop) / Pet chip (mobile) */}
        <div className="flex items-center shrink-0">
          {/* Desktop: Bean icon */}
          <Link
            to="/kodafellowship"
            className={cn("hidden lg:flex items-center justify-center rounded-xl hover:bg-gray-50 transition-colors", walkthroughActive && "invisible pointer-events-none")}
          >
            <BeanIcon width={64} height={64} beanColor={profile?.roomie_color ?? undefined} />
          </Link>
          {/* Mobile: Pet switcher chip, or glyph if no pet yet */}
          <div className="lg:hidden">
            <button
              type="button"
              onClick={() => setSwitcherOpen(true)}
              className="flex items-center gap-2 pl-1 pr-3 py-1.5 rounded-xl hover:bg-gray-50 transition-colors group max-w-[200px]"
              aria-label="Switch pet"
            >
              {activePet ? (
                <TopBarSpeciesIcon species={activePet.species} color={activePet.color} />
              ) : (
                <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                  <PawPrint size={14} className="text-gray-400" />
                </div>
              )}
              <span
                className={cn(
                  'text-sm font-bold truncate group-hover:text-gray-600 transition-colors',
                  activePet ? '' : 'text-gray-400'
                )}
                style={activePet ? { color: petChipColor } : undefined}
              >
                {petChipLabel}
              </span>
              <ChevronDown size={13} className="text-gray-400 shrink-0 group-hover:text-primary-brown transition-colors" />
            </button>
          </div>
        </div>

        {/* Centre: Nav links (desktop only) */}
        <nav className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
          {([
            { to: '/vitals', icon: <PawPrint size={22} />, label: t('nav.records') },
            { to: '/meds', icon: <Pill size={22} />, label: t('nav.meds') },
            { to: '/library', icon: <BookHeart size={22} />, label: t('nav.guide') },
            { to: '/settings', icon: <Settings size={22} />, label: t('account.title') },
          ] as const).map(({ to, icon, label }) => {
            const active = to === '/library'
              ? location.pathname.startsWith('/library')
              : to === '/settings'
              ? location.pathname.startsWith('/settings')
              : location.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-xl text-base font-medium transition-colors',
                  active
                    ? 'bg-primary-brown/10 text-primary-brown'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                )}
              >
                {icon}
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Right: Pet chip (desktop only) + notifications */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setSwitcherOpen(true)}
            className="hidden lg:flex items-center gap-2 pl-1 pr-3 py-1.5 rounded-xl hover:bg-gray-50 transition-colors group max-w-[200px]"
            aria-label="Switch pet"
          >
            {activePet ? (
              <TopBarSpeciesIcon species={activePet.species} color={activePet.color} />
            ) : (
              <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                <PawPrint size={14} className="text-gray-400" />
              </div>
            )}
            <span
              className={cn(
                'text-sm font-bold truncate group-hover:text-gray-600 transition-colors',
                activePet ? '' : 'text-gray-400'
              )}
              style={activePet ? { color: petChipColor } : undefined}
            >
              {petChipLabel}
            </span>
            <ChevronDown size={13} className="text-gray-400 shrink-0 group-hover:text-primary-brown transition-colors" />
          </button>
          <NotificationCenter />
        </div>
        </div>
      </header>

      {/* Pet switcher overlay */}
      {switcherOpen && <PetSwitcher onClose={() => setSwitcherOpen(false)} />}
    </>
  );
}

